import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from 'react';
import styles from './Checkbox.module.css';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  label?: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
  error?: string | undefined;
  /**
   * NC-2 — тон отметки.
   *
   * `form` (по умолчанию) — канон контрола формы, `--color-primary`.
   * `selection` — отметка ВЫБОРА СТРОКИ в таблице: берёт тон выделения, тот же,
   * что у рельса и подложки строки. Одно состояние обязано иметь один тон;
   * пока флажок красился каноном формы, «строка выбрана» показывалось navy на
   * контроле и бронзой на строке одновременно.
   */
  tone?: 'form' | 'selection';
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate, error, id, className, disabled, checked, tone, ...rest },
  ref,
) {
  const autoId = useId();
  const cbId = id ?? autoId;

  const setRef = (el: HTMLInputElement | null) => {
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
    if (el) el.indeterminate = indeterminate ?? false;
  };

  return (
    <label
      htmlFor={cbId}
      className={[
        styles.wrap,
        tone === 'selection' ? styles.wrapSelection : '',
        disabled ? styles.wrapDisabled : '',
        error ? styles.wrapError : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={styles.controlRow}>
        <input
          ref={setRef}
          id={cbId}
          type="checkbox"
          className={styles.input}
          disabled={disabled}
          {...(checked !== undefined ? { checked } : {})}
          aria-invalid={error ? 'true' : undefined}
          {...rest}
        />
        <span
          className={[styles.indicator, indeterminate ? styles.indicatorIndeterminate : '']
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        >
          {indeterminate ? (
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M2 5h6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path
                d="M1.5 5.2l2.3 2.3L8.5 2.8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          )}
        </span>
        {label !== undefined && <span className={styles.label}>{label}</span>}
      </span>
      {description && <span className={styles.description}>{description}</span>}
      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </label>
  );
});
